/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on async data loading */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileText, FileImage, FileArchive, File, Lock, FolderOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { usePageTitle } from "@/hooks/usePageTitle";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  xls: FileText,
  xlsx: FileText,
  jpg: FileImage,
  jpeg: FileImage,
  png: FileImage,
  gif: FileImage,
  zip: FileArchive,
  rar: FileArchive,
  "7z": FileArchive,
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const CLUBS = {
  "dsv-niners": "DSV Niners Wels",
  "sdv-niners": "SDV Niners Wels",
  "bsv-niners": "BSV Niners Wels",
  "perchten": "D'Wösa Höllen Viecha",
  "soundtheatre": "Soundtheatre",
};

export default function DownloadsPage() {
  usePageTitle("Downloads");
  const [downloads, setDownloads] = useState([]);
  const [publicDownloads, setPublicDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("Alle");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Always load public downloads first
    loadPublicDownloads();
    
    const token = localStorage.getItem("member_token");
    if (token) {
      axios.get(`${API}/members/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => {
          setIsLoggedIn(true);
          loadDownloads(token);
        })
        .catch(() => {
          localStorage.removeItem("member_token");
          localStorage.removeItem("member");
          setIsLoggedIn(false);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const loadPublicDownloads = async () => {
    try {
      const res = await axios.get(`${API}/downloads/public`);
      setPublicDownloads(res.data);
    } catch (err) {
      toast.error("Öffentliche Downloads konnten nicht geladen werden");
    }
  };

  const loadDownloads = async (token) => {
    try {
      const res = await axios.get(`${API}/downloads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDownloads(res.data);
    } catch (err) {
      toast.error("Downloads konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (download, isPublic = false) => {
    try {
      let response;
      if (isPublic) {
        response = await axios.get(`${API}/downloads/public/${download.id}/file`, {
          responseType: 'blob'
        });
      } else {
        const token = localStorage.getItem("member_token");
        response = await axios.get(`${API}/downloads/${download.id}/file`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', download.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Datei konnte nicht heruntergeladen werden");
    }
  };

  // Combine categories from both public and private downloads
  const allDownloadsForCategories = isLoggedIn ? downloads : publicDownloads;
  const categories = ["Alle", ...new Set(allDownloadsForCategories.map(d => d.category))];
  const filteredDownloads = selectedCategory === "Alle" 
    ? (isLoggedIn ? downloads : publicDownloads)
    : (isLoggedIn ? downloads : publicDownloads).filter(d => d.category === selectedCategory);

  if (loading && publicDownloads.length === 0) {
    return (
      <div className="py-24 px-4 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show public downloads even without login
  const displayDownloads = isLoggedIn ? filteredDownloads : publicDownloads.filter(d => 
    selectedCategory === "Alle" || d.category === selectedCategory
  );

  return (
    <div className="py-12 md:py-20 px-4 md:px-8" data-testid="downloads-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <FolderOpen className="w-8 h-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
            Downloads
          </h1>
        </div>

        {/* Login hint for non-logged-in users */}
        {!isLoggedIn && (
          <div className="bg-card border border-white/10 p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Für weitere Downloads bitte anmelden
              </p>
            </div>
            <button onClick={() => navigate("/mitglied-login")}
              className="px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors">
              Anmelden
            </button>
          </div>
        )}

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-white/10 hover:border-white/20'
                }`}
                data-testid={`category-${cat}`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Downloads List */}
        {displayDownloads.length === 0 ? (
          <div className="text-center py-16 border border-white/10 bg-card">
            <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {isLoggedIn ? "Keine Downloads verfügbar." : "Keine öffentlichen Downloads verfügbar."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayDownloads.map(download => {
              const IconComponent = FILE_ICONS[download.file_type] || File;
              const isPublicDownload = !isLoggedIn || download.public;
              return (
                <div
                  key={download.id}
                  className="bg-card border border-white/10 p-4 md:p-6 hover:border-white/20 transition-colors"
                  data-testid={`download-${download.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg shrink-0">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-bold text-lg">{download.title}</h3>
                          {download.description && (
                            <p className="text-sm text-muted-foreground mt-1">{download.description}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {download.category}
                            </Badge>
                            {download.club_id && (
                              <Badge className="text-xs bg-primary/20 text-primary">
                                {CLUBS[download.club_id] || download.club_id}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {download.filename}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatFileSize(download.file_size)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(download, isPublicDownload && !isLoggedIn)}
                          className="shrink-0 px-4 py-2 bg-primary text-primary-foreground text-sm font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors flex items-center gap-2"
                          data-testid={`download-btn-${download.id}`}
                        >
                          <Download className="w-4 h-4" />
                          <span className="hidden sm:inline">Download</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
